
import EditorJS, { API } from '@editorjs/editorjs'
import Embed from '@editorjs/embed'
import Table from '@editorjs/table'
import Delimiter from '@editorjs/delimiter'
import Marker from '@editorjs/marker'
import Quote from '@editorjs/quote'
import VBITImage from '../Editor/vbit-image'
import { Header } from '../Editor/header/header'
import { List } from '../Editor/list'
import Undo from 'editorjs-undo'
import Paragraph from '../Editor/paragraph/paragraph'
import GeneralMixin from '@/_mixins/general'
import type { OutputData } from '@editorjs/editorjs'

type EditorData = {
    type: string
    data: OutputData
}

type Tool = string | { type: string; config: any }

const defaultTools = [
    'header',
    'linkTool',
    'quote',
    'list',
    'embed',
    'table',
    'marker',
    'underline',
    'delimiter',
]

export default {
    mixins: [GeneralMixin],
    name: 'RichTextField',
    props: {
        label: { type: String, default: '' },
        placeholder: { type: String, default: '' },
        readonly: { type: Boolean, default: false },
        required: { type: Boolean, default: false },
        hideDetails: { type: Boolean, default: false },
        tools: { type: Array, default: () => [] },
        value: { type: String, default: '' },
        variant: { type: String, default: 'outlined' }, // outlined, text
    },
    data() {
        return {
            editor: null as EditorJS | null,
            data: null as EditorData | null,
            valid: false,
        }
    },
    created() {
        const emptyData: EditorData = {
            type: 'editorjs',
            data: {
                time: 0,
                blocks: [],
                version: '2.26.5',
            },
        }

        if (this.value) {
            try {
                this.data = JSON.parse(this.value)

                // https://app.clickup.com/t/8692y9nhn
                if (this.data?.data && !this.readonly) {
                    const lastBlock = this.data.data.blocks[this.data?.data.blocks.length - 1]
                    if (lastBlock?.type !== 'paragraph') {
                        this.data.data.blocks.push({ type: 'paragraph', data: { text: '' } })
                    }
                }
            } catch {
                this.data = emptyData
            }
        } else {
            this.data = emptyData
        }
    },
    mounted() {
        this.editor = new EditorJS({
            holder: this.$refs.el as HTMLElement,
            data: this.data?.data,
            tools: this.getTools(),
            minHeight: 0,
            placeholder: this.placeholder,
            readOnly: this.readonly,
            onChange: this.handleChange,
            defaultBlock: 'paragraph',
            onReady: () => {
                new Undo({ editor: this.editor })
            },

            i18n: {
                messages: {
                    tools: {
                        list: {
                            Unordered: this.$t(
                                'form_fe["richTextField_text_toolUnorderedList"]'
                            ).toString(),
                            Ordered: this.$t(
                                'form_fe["richTextField_text_toolOrderedList"]'
                            ).toString(),
                        },
                    },
                    ui: {
                        popover: {
                            Filter: this.$t(
                                'form_fe["richTextField_placeholder_popoverFilter"]'
                            ).toString(),
                            'Nothing found': this.$t(
                                'form_fe["richTextField_text_popoverNoResult"]'
                            ).toString(),
                        },
                        blockTunes: {
                            toggler: {
                                'Click to tune': this.$t(
                                    'form_fe["richTextField_tooltip_clickToTune"]'
                                ).toString(),
                            },
                        },
                        inlineToolbar: {
                            converter: {
                                'Convert to': this.$t(
                                    'form_fe["richTextField_text_inlineToolbarConvertTo"]'
                                ).toString(),
                            },
                        },
                        toolbar: {
                            toolbox: {
                                Add: this.$t('form_fe["richTextField_tooltip_add"]').toString(),
                            },
                        },
                    },
                    toolNames: {
                        Text: this.$t('form_fe["richTextField_text_toolText"]').toString(),
                        Heading1: this.$t('form_fe["richTextField_text_toolTitle"]', {
                            level: 1,
                        }).toString(),
                        Heading2: this.$t('form_fe["richTextField_text_toolTitle"]', {
                            level: 2,
                        }).toString(),
                        Heading3: this.$t('form_fe["richTextField_text_toolHeading"]', {
                            level: 1,
                        }).toString(),
                        Heading4: this.$t('form_fe["richTextField_text_toolHeading"]', {
                            level: 2,
                        }).toString(),
                        Heading5: this.$t('form_fe["richTextField_text_toolHeading"]', {
                            level: 3,
                        }).toString(),
                        Heading6: this.$t('form_fe["richTextField_text_toolSubTitle"]').toString(),
                        Unordered: this.$t(
                            'form_fe["richTextField_text_toolUnorderedList"]'
                        ).toString(),
                        Ordered: this.$t(
                            'form_fe["richTextField_text_toolOrderedList"]'
                        ).toString(),
                        Quote: this.$t('form_fe["richTextField_text_toolQuote"]').toString(),
                        Table: this.$t('form_fe["richTextField_text_toolTable"]').toString(),
                        Link: this.$t('form_fe["richTextField_text_toolLink"]').toString(),
                        Marker: this.$t('form_fe["richTextField_text_toolMarker"]').toString(),
                        Bold: this.$t('form_fe["richTextField_text_toolBold"]').toString(),
                        Italic: this.$t('form_fe["richTextField_text_toolItalic"]').toString(),
                    },
                    blockTunes: {
                        delete: {
                            Delete: this.$t(
                                'form_fe["richTextField_text_blockTuneDelete"]'
                            ).toString(),
                        },
                        moveUp: {
                            'Move up': this.$t(
                                'form_fe["richTextField_text_blockTuneMoveUp"]'
                            ).toString(),
                        },
                        moveDown: {
                            'Move down': this.$t(
                                'form_fe["richTextField_text_blockTuneMoveDown"]'
                            ).toString(),
                        },
                    },
                },
            },
        })
    },
    beforeDestroy() {
        this.editor?.destroy?.()
    },
    computed: {
        isError() {
            return this.required && this.valid
        },
        rules() {
            return {
                required: (value: EditorData) => {
                    const validationResult =
                        value.data.blocks.length > 0 ||
                        this.$t('fe_validation.error_required').toString()
                    this.valid = validationResult !== true
                    return validationResult
                },
            }
        },
    },
    methods: {
        async handleChange(api: API) {
            const blockCount = api.blocks.getBlocksCount()
            if (blockCount > 0) {
                const lastBlock = api.blocks.getBlockByIndex(blockCount - 1)

                // https://app.clickup.com/t/869514v6d
                if (lastBlock?.name !== 'paragraph' && lastBlock?.name !== 'header') {
                    const blockType = 'paragraph'
                    const blockData = await api.blocks.composeBlockData(blockType)
                    api.blocks.insert(blockType, blockData, undefined, blockCount, false)
                }
            }

            if (!this.data) return

            this.data.data = await api.saver.save()
            this.$emit('input', this.data.data.blocks.length > 0 ? JSON.stringify(this.data) : null)
            this.$nextTick(() => {
                // @ts-ignore
                this.$refs.inputEl.validate()
            })
        },
        focus() {
            if (this.readonly) return
            this.editor?.isReady.then(() => {
                this.editor?.caret.setToLastBlock('end')
            })
        },
        scrollIntoView() {
            ;(this.$refs.el as HTMLElement)?.scrollIntoView()
        },
        getTools() {
            const tools = {}
            const allTools = [...defaultTools, ...this.tools] as Tool[]
            Object.assign(tools, {
                paragraph: {
                    class: Paragraph,
                    inlineToolbar: true,
                },
            })
            for (const tool of allTools) {
                if (typeof tool === 'string') {
                    switch (tool) {
                        case 'header':
                            Object.assign(tools, {
                                header: {
                                    class: Header,
                                    inlineToolbar: true,
                                },
                            })
                            break
                        case 'list':
                            Object.assign(tools, {
                                list: {
                                    class: List,
                                    inlineToolbar: true,
                                },
                            })
                            break
                        case 'embed':
                            Object.assign(tools, { embed: Embed })
                            break
                        case 'quote':
                            Object.assign(tools, { quote: Quote })
                            break
                        case 'table':
                            Object.assign(tools, { table: { class: Table, inlineToolbar: true } })
                            break
                        case 'delimiter':
                            Object.assign(tools, { delimiter: Delimiter })
                            break
                        case 'marker':
                            Object.assign(tools, {
                                Marker: {
                                    class: Marker,
                                    shortcut: 'CMD+SHIFT+M',
                                },
                            })
                            break
                    }
                } else {
                    switch (tool.type) {
                        case 'VBITImage':
                            Object.assign(tools, {
                                vbitImage: {
                                    class: VBITImage,
                                    config: {
                                        ...tool.config,
                                        onRemoved: (id: string) => this.$emit('remove:image', id),
                                    },
                                },
                            })
                            break
                    }
                }
            }
            return tools
        },
        handleDrop(event: DragEvent) {
            if (this.readonly || !event.dataTransfer || event.dataTransfer.files.length > 1) return
            this.editor?.blocks.insert('vbitImage', { file: event.dataTransfer.files[0] })
        },
    },
}
